import { FC, useEffect, useState, useContext } from 'react';
import clsx from 'clsx';
import { CarouselContext, CarouselProvider, Slide, Slider } from 'pure-react-carousel';
import { baseVariables, Button, MapComponent, MapMarker, Modal, toRem, Types } from '@marriott/mi-ui-library';
import { HotelBasicInformation, HotelMediaContent } from '@marriott/mi-groups-graphql';
import { Marker } from '../../../molecules/MapView/MapView.types';
import { useMediaQuery } from '../../../hooks';
import {
  getPropertyLargestRoomArea,
  getPropertyAirportDistanceLabel,
  getPropertyItemValue,
  getReviewsUrl,
  getAreaValueAndUnitForLocale,
  isAllowedForLocale,
} from '../../../utils';
import { useRfpTrayStore, useLocaleStore } from '../../../store';
import { PropertyCardType, PropertyCard } from '../../../molecules';
import { RfpProperty } from '../RfpCard/RfpCard.types';
import { CompareHotelsModalProps, PropertyCarouselProps } from './CompareHotelsModal.types';
import { StyledCompareHotelsModal } from './CompareHotelsModal.styles';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';

export const CarouselNavigation = ({ totalSlides }: { totalSlides: number }) => {
  const carouselContext = useContext(CarouselContext);
  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    function onChange() {
      const currentSlide = carouselContext.state.currentSlide;
      setActiveSlide(Math.ceil(currentSlide));
    }
    carouselContext?.subscribe(onChange);
    return () => carouselContext?.unsubscribe(onChange);
  }, [carouselContext]);

  return (
    <div className="d-flex justify-content-center">
      <div className="carouselControlType3b">
        <div>
          {Array.from(Array(totalSlides)).map((_, index) => (
            <Button
              key={index}
              ariaLabel={`carousel-nav-${index + 1}`}
              className={clsx('carousel-nav', activeSlide === index && 'is-active')}
              testId={`carousel-nav-${index}`}
              callback={() => carouselContext.setStoreState({ currentSlide: index })}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export const PropertyCarousel: FC<PropertyCarouselProps> = ({ labels, properties }) => {
  const isTablet = useMediaQuery(baseVariables.mediaQuery.md);

  const propertyCard = (property: RfpProperty) => {
    const { seoNickname, media, reviews, airports, basicInformation, meetingRooms } = property;
    const { brand, name, numberOfRooms, numberOfSuites } = (basicInformation as HotelBasicInformation) || {};

    const reviewsAndDistance = {
      rating: `${getPropertyItemValue(reviews?.stars?.count || '')}`,
      reviewCount: reviews?.numberOfReviews?.count
        ? `${getPropertyItemValue(reviews.numberOfReviews.count)} ${labels.reviews}`
        : '',
      reviewLink: getReviewsUrl(seoNickname || '', brand?.id),
      reviewTooltipText: labels.reviewTooltipText,
      distance: getPropertyAirportDistanceLabel(airports || [], labels.toAirport, true),
    };

    const getPropertyAttributes = () => {
      const attributes = [];

      if (meetingRooms?.totalNumberOfMeetingRooms) {
        attributes.push({
          label: labels.meetingEventRooms,
          value: `${meetingRooms.totalNumberOfMeetingRooms}`,
        });
      }

      if (numberOfRooms || numberOfSuites) {
        attributes.push({
          label: labels.guestRooms,
          value: getPropertyItemValue(parseInt(numberOfRooms ?? '0', 10) + parseInt(numberOfSuites ?? '0', 10)),
        });
      }

      const eventSpaceArea = meetingRooms?.totalAreaOfMeetingSpace;
      const eventSpaceAreaUnit = meetingRooms?.areaUnit;

      if (eventSpaceArea) {
        const { area, unit } = getAreaValueAndUnitForLocale(eventSpaceArea, eventSpaceAreaUnit!, labels.spaceUnit);
        attributes.push({
          label: labels.totalEventSpace,
          value: getPropertyItemValue(area, unit),
        });
      }

      const largestRoomArea = getPropertyLargestRoomArea(meetingRooms?.edges || []);
      const largestRoomAreaUnit = meetingRooms?.areaUnit;

      if (largestRoomArea) {
        const { area, unit } = getAreaValueAndUnitForLocale(+largestRoomArea, largestRoomAreaUnit!, labels.spaceUnit);
        attributes.push({
          label: labels.largestRoom,
          value: getPropertyItemValue(area, unit),
        });
      }

      return attributes;
    };

    return (
      <div key={property.id} className="pb-3 card-container">
        <PropertyCard
          id={property.id}
          type={PropertyCardType.COMPACT}
          media={media as HotelMediaContent}
          mediaLabels={{
            carouselAriaLabel: labels.carouselAriaLabel,
            carouselImageAriaLabel: labels.carouselImageAriaLabel,
            logoAriaLabel: labels.logoAriaLabel,
          }}
          brand={brand}
          propertyName={{
            title: name || '',
          }}
          reviewsAndDistance={reviewsAndDistance}
          showDescription={true}
          description={getPropertyAttributes()}
        />
      </div>
    );
  };

  return (
    <div data-testid="property-card-carousel">
      {isTablet && properties?.length === 3 ? (
        <CarouselProvider
          naturalSlideWidth={10}
          naturalSlideHeight={640}
          totalSlides={properties?.length}
          visibleSlides={2}
          isIntrinsicHeight={true}
          step={1}
          dragStep={1}
        >
          <Slider classNameTray="slider">
            {properties?.map((property, index) => (
              <Slide key={property.id} index={index} className="slide">
                {propertyCard(property)}
              </Slide>
            ))}
          </Slider>
          <CarouselNavigation totalSlides={2} />
        </CarouselProvider>
      ) : (
        <div className={clsx('d-md-flex justify-content-md-between')}>
          {properties?.map(property => propertyCard(property))}
        </div>
      )}
    </div>
  );
};

export const CompareHotelsModal: FC<CompareHotelsModalProps> = props => {
  const { onClose, show, labels } = props;
  const { color, font } = baseVariables;

  const apiKey = useClientEnvVarsStore.getState().envVarsObject['GOOGLE_MAP_API_KEY'] ?? '';
  const mapId = process.env['GOOGLE_MAP_ID'] ?? '';

  const isTablet = useMediaQuery(baseVariables.mediaQuery.md);

  const { locale } = useLocaleStore();
  const { properties } = useRfpTrayStore();

  const availableProperties = properties.filter(property => !property?.isMarkForRemoved);

  const markers = availableProperties.map(property => ({
    id: property.id,
    name: property.basicInformation?.name || '',
    lat: +((property.basicInformation as HotelBasicInformation)?.latitude ?? 0),
    lng: +((property.basicInformation as HotelBasicInformation)?.longitude ?? 0),
    isActive: false,
  }));

  const center = {
    lat: markers[0].lat ?? 0,
    lng: markers[0].lng ?? 0,
  };

  return (
    <StyledCompareHotelsModal>
      <Modal
        modalId="compareHotelsModal"
        role="dialog"
        labelledBy="compareHotelsModal"
        secondaryClassName="compare-hotels-modal"
        show={show}
        popupOpenState={show}
        handleBlur={true}
        disableScrollOnBody={true}
        setPopupOpenState={onClose}
        onClose={onClose}
      >
        <Modal.Header
          headingTagElement={Types.tags.div}
          labelText={labels.title}
          popupHeaderOnCLoseFunc={onClose}
          closeIconClickTrackValue="no tracking"
        ></Modal.Header>
        <Modal.Body className="custom-scrollbar mt-3 px-3 px-md-4 modal-body">
          <>
            <PropertyCarousel labels={labels} properties={availableProperties} />
            {isAllowedForLocale(locale) && (
              <div className="map-container">
                <MapComponent
                  googleMapsApiKey={apiKey}
                  mapId={mapId}
                  mapContainerStyle={{ height: '100%', width: '100%' }}
                  mapHeight={isTablet ? toRem(420) : toRem(270)}
                  zoom={15}
                  center={center}
                  options={{
                    fullscreenControl: true,
                    zoomControl: true,
                    gestureHandling: 'greedy',
                    mapTypeControlOptions: {
                      mapTypeIds: [window.google.maps.MapTypeId.ROADMAP, window.google.maps.MapTypeId.SATELLITE],
                      style: window.google.maps.MapTypeControlStyle.DROPDOWN_MENU,
                    },
                  }}
                  children={markers?.map((marker: Marker) => (
                    <MapMarker
                      key={marker.id}
                      label={{
                        text: marker.name,
                        fontSize: font.fontS,
                        color: color.base10,
                        className: 'm-map-pin map-marker',
                      }}
                      position={{ lat: marker.lat, lng: marker.lng }}
                    />
                  ))}
                />
              </div>
            )}
          </>
        </Modal.Body>
        <Modal.FooterGeneric className="modal-footer" />
      </Modal>
    </StyledCompareHotelsModal>
  );
};
